/**
 * Gmail-specific message types and schemas
 */

import { z } from "zod";
import { MessageBaseSchema } from "../message";

// ============================================================================
// GMAIL MESSAGE METADATA SCHEMAS
// ============================================================================

export const GmailMessageMetadataSchema = z.object({
  labels: z.array(z.string()).optional(),
  threadId: z.string().optional(),
  messageId: z.string().optional(),
  historyId: z.string().optional(),
  internalDate: z.string().optional(),
  sizeEstimate: z.number().int().nonnegative().optional(),
  snippet: z.string().optional(),
  mimeType: z.string().optional(),
  headers: z.record(z.string(), z.string()).optional(),
  parts: z
    .array(
      z.object({
        partId: z.string(),
        mimeType: z.string(),
        filename: z.string().optional(),
        headers: z.record(z.string(), z.string()).optional(),
        body: z
          .object({
            size: z.number().int().nonnegative(),
            data: z.string().optional(),
            attachmentId: z.string().optional(),
          })
          .optional(),
      })
    )
    .optional(),
  payload: z
    .object({
      partId: z.string().optional(),
      mimeType: z.string().optional(),
      filename: z.string().optional(),
      headers: z.record(z.string(), z.string()).optional(),
      body: z
        .object({
          size: z.number().int().nonnegative(),
          data: z.string().optional(),
          attachmentId: z.string().optional(),
        })
        .optional(),
      parts: z.array(z.any()).optional(),
    })
    .optional(),
}).strict();

export type GmailMessageMetadata = z.infer<typeof GmailMessageMetadataSchema>;

// ============================================================================
// GMAIL MESSAGE SCHEMAS
// ============================================================================

export const GmailMessageSchema = MessageBaseSchema.extend({
  provider: z.literal("google-mail"),
  threadId: z.string().optional(),
  labelIds: z.array(z.string()).optional(),
  starred: z.boolean().optional(),
  spam: z.boolean().optional(),
  trash: z.boolean().optional(),
  draft: z.boolean().optional(),
  metadata: GmailMessageMetadataSchema,
}).strict();

export type GmailMessage = z.infer<typeof GmailMessageSchema>;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Type guard to check if a message is a Gmail message
 */
export function isGmailMessage(message: any): message is GmailMessage {
  return message?.provider === "google-mail";
}

/**
 * Validate a Gmail message
 */
export function validateGmailMessage(data: unknown): GmailMessage {
  return GmailMessageSchema.parse(data);
}
